import { BufferAttribute, BufferGeometry } from 'three';
import type { Vec3 } from '../../geo/types';
import { createMeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SHIP_PAINTER_SCALE_H, SHIP_PAINTER_SCALE_V } from './shipPlacement';
import type { SurfaceMesh } from './surfaceNet';

/* The painter camera draws a ship 2.3 times wider and 3.5 times taller than it stands. There the
   cloud is first flattened by H/V towards the water, so the painter draws its billows round, as the
   painting does. */
const FLATTENING = SHIP_PAINTER_SCALE_H / SHIP_PAINTER_SCALE_V;
/* A salute is the painted moment, drawn in the painter view alone (the ship's mesh leaves it out
   of what it draws while the painter amount is 0): as that view comes in, each cloud grows from
   this share of itself at its end nearest the ship, where the gun fires, to its whole. */
const GROWN_FROM = 0.1;

/* The attributes the builder gives every vertex of a style, read off a face it builds in it. */
function styleVertex(style: VertexStyle): BufferGeometry {
  const builder = createMeshBuilder(3);
  const face: Vec3[] = [
    [0, 0, 0],
    [0, 0, 1],
    [1, 0, 0],
  ];
  builder.polygon(style, { origin: [0, 0, 0], yaw: 0 }, face);
  return builder.build();
}

/* The first `size` values, `count` times over. */
function repeated(values: ArrayLike<number>, size: number, count: number): Float32Array {
  const out = new Float32Array(count * size);
  for (let i = 0; i < out.length; i++) out[i] = values[i % size]!;
  return out;
}

/* Every point of the clouds in the attributes the builder gives a vertex of the style, but for
   those the smoke sets itself. */
function styled(count: number, style: VertexStyle, own: readonly string[]): BufferGeometry {
  const geometry = new BufferGeometry();
  Object.entries(styleVertex(style).attributes)
    .filter(([name]) => !own.includes(name))
    .forEach(([name, attribute]) => {
      const size = attribute.itemSize;
      geometry.setAttribute(
        name,
        new BufferAttribute(repeated(attribute.array, size, count), size)
      );
    });
  return geometry;
}

/* The vectors of every list, one after another, flat. */
function flat(lists: readonly (readonly Vec3[])[], count: number): Float32Array {
  const values = new Float32Array(3 * count);
  let at = 0;
  for (const list of lists) {
    for (const v of list) {
      values[at] = v[0];
      values[at + 1] = v[1];
      values[at + 2] = v[2];
      at += 3;
    }
  }
  return values;
}

/* A cloud grown from GROWN_FROM of itself at its end nearest the ship. */
function sprouted(cloud: SurfaceMesh): Vec3[] {
  const gun = cloud.points.reduce((a, b) => (Math.abs(b[0]) < Math.abs(a[0]) ? b : a));
  return cloud.points.map((p) => [
    gun[0] + GROWN_FROM * (p[0] - gun[0]),
    gun[1] + GROWN_FROM * (p[1] - gun[1]),
    gun[2] + GROWN_FROM * (p[2] - gun[2]),
  ]);
}

/* The painter view's cloud: flattened by H/V towards the water, so the ship's painter scales draw
   it round. */
function flattened(cloud: SurfaceMesh): Vec3[] {
  return cloud.points.map((p) => [p[0], FLATTENING * p[1], p[2]]);
}

/**
 * The clouds of a salute as one geometry in the smoke's style: each point of a cloud one vertex,
 * shared by the faces around it, at its smooth normal. The offset the painter amount adds carries
 * each cloud from a tenth of itself at the gun to its whole, flattened towards the water.
 */
export function smokeGeometry(clouds: readonly SurfaceMesh[], style: VertexStyle): BufferGeometry {
  const count = clouds.reduce((n, cloud) => n + cloud.points.length, 0);
  const geometry = styled(count, style, ['position', 'normal']);
  const position = flat(clouds.map(sprouted), count);
  geometry.setAttribute('position', new BufferAttribute(position, 3));
  const normal = flat(
    clouds.map((cloud) => cloud.normals),
    count
  );
  geometry.setAttribute('normal', new BufferAttribute(normal, 3));
  const drawn = flat(clouds.map(flattened), count);
  const offset = drawn.map((value, i) => value - position[i]!);
  geometry.setAttribute('aAxisOffset', new BufferAttribute(offset, 3));
  const index = new Uint32Array(clouds.reduce((n, cloud) => n + 3 * cloud.faces.length, 0));
  clouds.reduce(
    ({ at, first }, cloud) => {
      cloud.faces.forEach((face, f) => {
        for (let k = 0; k < 3; k++) index[at + 3 * f + k] = face[k]! + first;
      });
      return { at: at + 3 * cloud.faces.length, first: first + cloud.points.length };
    },
    { at: 0, first: 0 }
  );
  geometry.setIndex(new BufferAttribute(index, 1));
  return geometry;
}
